/**
 * 달력 날짜 계산. 날짜는 "YYYY-MM-DD" 문자열(조직 시간대 기준 날짜)로 다룬다.
 * 시간대가 끼어들지 않도록 내부 계산은 모두 UTC 자정 기준이다.
 */

export type DateStr = string;

/** 조직 시간대 (단일 조직, 한국) */
export const ORG_TIMEZONE = "Asia/Seoul";

export const WEEKDAY_NAMES = ["월", "화", "수", "목", "금", "토", "일"] as const;

const DAY_MS = 86_400_000;

export function isDateStr(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s) && toDateStr(fromDateStr(s)) === s;
}

function fromDateStr(s: DateStr): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!));
}

function toDateStr(d: Date): DateStr {
  return d.toISOString().slice(0, 10);
}

export function makeDate(year: number, month: number, day: number): DateStr {
  return toDateStr(new Date(Date.UTC(year, month - 1, day)));
}

export function dateParts(s: DateStr): { year: number; month: number; day: number } {
  const [year, month, day] = s.split("-").map(Number);
  return { year: year!, month: month!, day: day! };
}

export function addDays(s: DateStr, n: number): DateStr {
  return toDateStr(new Date(fromDateStr(s).getTime() + n * DAY_MS));
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** n달 뒤 같은 날. 그 달에 없는 날이면 말일로 (1/31 + 1달 = 2/28) */
export function addMonths(s: DateStr, n: number): DateStr {
  const { year, month, day } = dateParts(s);
  const total = year * 12 + (month - 1) + n;
  const y = Math.floor(total / 12);
  const m = (total % 12) + 1;
  return makeDate(y, m, Math.min(day, daysInMonth(y, m)));
}

/** 0=월 … 6=일 */
export function weekday(s: DateStr): number {
  return (fromDateStr(s).getUTCDay() + 6) % 7;
}

/** 그 주의 월요일 */
export function mondayOf(s: DateStr): DateStr {
  return addDays(s, -weekday(s));
}

export function daysBetween(a: DateStr, b: DateStr): number {
  return Math.round((fromDateStr(b).getTime() - fromDateStr(a).getTime()) / DAY_MS);
}

export function monthsBetween(a: DateStr, b: DateStr): number {
  const pa = dateParts(a);
  const pb = dateParts(b);
  return (pb.year - pa.year) * 12 + (pb.month - pa.month);
}

export function lastDayOfMonth(s: DateStr): DateStr {
  const { year, month } = dateParts(s);
  return makeDate(year, month, daysInMonth(year, month));
}

/** 지금 조직 시간대의 날짜와 하루 중 분 */
export function nowInZone(now = new Date(), timeZone = ORG_TIMEZONE): { date: DateStr; minutes: number } {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)!.value;
  return { date: `${get("year")}-${get("month")}-${get("day")}`, minutes: Number(get("hour")) * 60 + Number(get("minute")) };
}

export function todayInZone(now = new Date(), timeZone = ORG_TIMEZONE): DateStr {
  return nowInZone(now, timeZone).date;
}

/** "HH:MM" ↔ 분 */
export function timeToMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h! * 60 + m!;
}

export function minutesToTime(min: number): string {
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
}

/**
 * 조직 시간대의 날짜+시각을 실제 시각(UTC)으로. 한국은 서머타임이 없어 오프셋이 고정이지만,
 * 다른 시간대에서도 맞도록 오프셋을 계산해 맞춘다.
 */
export function zonedToInstant(date: DateStr, time: string | null, timeZone = ORG_TIMEZONE): Date {
  const { year, month, day } = dateParts(date);
  const min = time ? timeToMinutes(time) : 0;
  const guess = Date.UTC(year, month - 1, day, Math.floor(min / 60), min % 60);
  const offset = (t: number) => {
    const z = nowInZone(new Date(t), timeZone);
    const p = dateParts(z.date);
    return Date.UTC(p.year, p.month - 1, p.day, 0, z.minutes) - t;
  };
  const first = guess - offset(guess);
  return new Date(guess - offset(first));
}
